// MarkSentExternallyControl — shared control for WS2, WS3, WS7
// completion-gating communications.  Renders a checkbox next to the
// primary action button; ticking opens a modal to capture date-sent +
// optional notes + mandatory confirmation.
//
// Props:
//   label              — button/toggle label
//   description        — small helper under the checkbox
//   endpointUrl        — absolute URL to POST { ...formData } to
//   buildBody(data)    — (date, notes, opId) → request body object
//   onSuccess(result)  — callback after the server returns 2xx
//   disabled           — grey out when gate already satisfied
//   testid             — root data-testid prefix
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const _uuid4 = () =>
    (crypto && crypto.randomUUID)
        ? crypto.randomUUID()
        : "x-" + Date.now() + "-" + Math.random().toString(36).slice(2);

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function MarkSentExternallyControl({
    label = "Mark as sent externally",
    description = "If you've already sent this communication to the client outside the platform (e.g. by Gmail), tick to record it here. Fully satisfies the completion requirement.",
    endpointUrl,
    buildBody,
    onSuccess,
    disabled = false,
    alreadySatisfied = false,
    satisfiedSummary = null,
    testid = "mark-sent-externally",
}) {
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState(todayIso());
    const [notes, setNotes] = useState("");
    const [confirmed, setConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [opId] = useState(() => _uuid4());

    const close = () => {
        if (busy) return;
        setOpen(false);
        setConfirmed(false);
    };

    const submit = async () => {
        if (!confirmed || busy) return;
        setBusy(true);
        try {
            const body = buildBody({ sent_on: date, notes: notes.trim() || null, op_id: opId });
            const r = await axios.post(endpointUrl, body, { withCredentials: true });
            toast.success("Recorded as sent externally.");
            setOpen(false);
            if (onSuccess) await onSuccess(r.data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string"
                ? detail
                : (detail?.error || "Unable to record external send."));
            setBusy(false);
        }
    };

    if (alreadySatisfied) {
        return (<div className="flex flex-col gap-1" data-testid={`${testid}-satisfied`}>
            <div className="inline-flex items-center gap-2 text-[12.5px] text-[#555]">
                <span className="inline-flex rounded-full bg-[#F0EBE1] px-2 py-0.5 text-[10.5px] uppercase tracking-wide text-[#5A4B2A]"
                      data-testid={`${testid}-badge`}>Sent externally</span>
                {satisfiedSummary && <span className="text-[#6B6B65]">{satisfiedSummary}</span>}
            </div>
        </div>);
    }

    return (<div className="flex flex-col gap-1" data-testid={`${testid}-wrap`}>
        <label className="inline-flex items-start gap-2 cursor-pointer select-none">
            <input type="checkbox" disabled={disabled}
                   checked={open}
                   onChange={(e) => { if (e.target.checked) setOpen(true); else setOpen(false); }}
                   data-testid={`${testid}-toggle`}
                   className="mt-0.5 h-4 w-4 accent-[#A8543F]" />
            <div className="flex flex-col gap-0.5">
                <span className="text-[13px] text-charcoal">{label}</span>
                {description && <span className="text-[11.5px] text-[#777] leading-snug">{description}</span>}
            </div>
        </label>
        {open && (<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                        data-testid={`${testid}-modal`}>
            <div className="bg-white rounded-lg shadow-xl w-[520px] max-w-[95vw] p-5 flex flex-col gap-4">
                <div>
                    <div className="font-manrope font-semibold text-[15px] text-charcoal">Record external send</div>
                    <div className="text-[12px] text-[#666] mt-1">Capture when and how this communication was sent to the client outside the platform.</div>
                </div>
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wide text-[#888]">Date sent</span>
                    <input type="date" value={date} max={todayIso()}
                           onChange={e => setDate(e.target.value)}
                           data-testid={`${testid}-date`}
                           className="h-9 rounded-md border border-[#E5E2DC] bg-white px-2 text-[13px]" />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wide text-[#888]">Notes (optional)</span>
                    <textarea value={notes} onChange={e => setNotes(e.target.value.slice(0, 1000))}
                              rows={3}
                              placeholder="e.g. Sent via Gmail from hello@. Attached cover letter."
                              data-testid={`${testid}-notes`}
                              className="rounded-md border border-[#E5E2DC] bg-white px-2 py-1.5 text-[13px]" />
                    <span className="text-[11px] text-[#999]">{notes.length}/1000</span>
                </label>
                <label className="inline-flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={confirmed}
                           onChange={e => setConfirmed(e.target.checked)}
                           data-testid={`${testid}-confirm`}
                           className="mt-0.5 h-4 w-4 accent-[#A8543F]" />
                    <span className="text-[12.5px] text-charcoal">
                        I confirm this communication was sent to the client outside the platform.
                    </span>
                </label>
                <div className="flex items-center justify-end gap-2">
                    <button type="button" onClick={close} disabled={busy}
                            data-testid={`${testid}-cancel`}
                            className="text-[13px] px-3 py-1.5 border border-[#C7BEB0] rounded-md hover:bg-[#F5F3EE] disabled:opacity-40">
                        Cancel
                    </button>
                    <button type="button" onClick={submit}
                            disabled={!confirmed || busy}
                            data-testid={`${testid}-submit`}
                            className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">
                        {busy ? "Recording…" : "Confirm"}
                    </button>
                </div>
            </div>
        </div>)}
    </div>);
}
